import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';

// Reusable zod building blocks. Messages are i18n KEYS, not text — the field
// components resolve them with t() at render time, so validation copy is
// localized automatically.

// Strict e-mail — local@domain.tld, no spaces. Shared by vEmail and the CSV import
// so single-user and bulk registration validate addresses identically.
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
export const isValidEmail = (value: string): boolean => EMAIL_RE.test(value.trim());

export const vEmail = z
    .string()
    .trim()
    .min(1, 'validation.required')
    .max(254, 'validation.maxLength')
    .refine(isValidEmail, 'validation.email');

export const vRequired = (max = 256) =>
    z.string().trim().min(1, 'validation.required').max(max, 'validation.maxLength');

/** Optional free-text: empty is allowed, only the max length is enforced. */
export const vOptional = (max = 256) => z.string().trim().max(max, 'validation.maxLength');

/** Strong password for set/reset/confirm flows. */
export const vPassword = z
    .string()
    .min(8, 'validation.passwordWeak')
    .regex(/[a-z]/, 'validation.passwordWeak')
    .regex(/[A-Z]/, 'validation.passwordWeak')
    .regex(/\d/, 'validation.passwordWeak');

/** Login password: only "present", strength is the backend's concern there. */
export const vLoginPassword = z.string().min(1, 'validation.required');

/** One-time 2FA code: digits only, typically 6. */
export const vOtp = z
    .string()
    .trim()
    .min(4, 'validation.minLength')
    .max(8, 'validation.maxLength')
    .regex(/^\d+$/, 'validation.digitsOnly');

/** International phone in E.164 (from PhoneField). Rejects malformed numbers up
 *  front so they don't fail later, server-side, at Authy registration. */
export const vPhone = z
    .string()
    .min(1, 'validation.required')
    .refine((v) => isValidPhoneNumber(v), 'validation.phone');
